"use client";

import React, { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis, Cell } from "recharts";
import { TrendingUp, Users, Building2, DollarSign, ArrowUpRight, Activity, MapPin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const chartData = [{
  state: "California",
  population: 15500000,
  label: "15.5M"
}, {
  state: "Texas",
  population: 11500000,
  label: "11.5M"
}, {
  state: "Florida",
  population: 5700000,
  label: "5.7M"
}, {
  state: "New York",
  population: 3800000,
  label: "3.8M"
}, {
  state: "Illinois",
  population: 2300000,
  label: "2.3M"
}, {
  state: "Arizona",
  population: 2300000,
  label: "2.3M"
}, {
  state: "New Jersey",
  population: 2100000,
  label: "2.1M"
}, {
  state: "Colorado",
  population: 1400000,
  label: "1.4M"
}, {
  state: "New Mexico",
  population: 1000000,
  label: "1.0M"
}, {
  state: "Georgia",
  population: 1000000,
  label: "1.0M"
}];
const chartConfig = {
  population: {
    label: "Hispanic Population",
    color: "var(--chart-1)"
  },
  colorado: {
    label: "Colorado",
    color: "var(--chart-2)"
  }
} satisfies ChartConfig;
export default function StateMarketRankings() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const maxVal = Math.max(...chartData.map(d => d.population));
  return <section id="state-market-rankings" ref={ref} className={`dark bg-background text-foreground py-20 relative overflow-hidden transition-opacity duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-secondary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          
          {}
          <div className="w-full lg:w-1/3 flex flex-col gap-8">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-medium uppercase tracking-wider mb-4 border border-secondary/20">
                <Activity className="w-3 h-3" />
                Market Intelligence
              </div>
              <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 leading-tight">
                Colorado: The <span className="text-secondary">8th Largest</span> Hispanic Market
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                With over $40B in purchasing power and a rapidly expanding entrepreneurial ecosystem, Colorado is a national leader in Hispanic economic influence.
              </p>
            </div>

            {}
            <Card className="border-border/50 shadow-lg bg-card/50 backdrop-blur-sm">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-xl">
                  <span>Growth Tracker</span>
                  <span className="relative flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-chart-1 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-chart-1"></span>
                  </span>
                </CardTitle>
                <CardDescription>Real-time economic indicators</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-6 pt-4">
                {[{
                label: "Hispanic-Owned Businesses",
                value: "120,000+",
                icon: Building2,
                trend: "+12% YoY",
                color: "text-chart-1"
              }, {
                label: "State Purchasing Power",
                value: "$40B+",
                icon: DollarSign,
                trend: "Growing",
                color: "text-chart-2"
              }, {
                label: "DPS Hispanic Students",
                value: "58%",
                icon: Users,
                trend: "Future Workforce",
                color: "text-chart-4"
              }].map((item, i) => <div key={i} data-index={i} className="flex items-center gap-4 group">
                    <div className={`p-3 rounded-lg bg-muted group-hover:bg-accent transition-colors ${item.color}`}>
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground font-medium">{item.label}</p>
                      <div className="flex items-baseline gap-2">
                        <h4 className="text-2xl font-bold tracking-tight">{item.value}</h4>
                        <span className="text-xs font-medium text-emerald-500 flex items-center">
                          {item.trend} <ArrowUpRight className="w-3 h-3 ml-0.5" />
                        </span>
                      </div>
                    </div>
                  </div>)}
              </CardContent>
              <Separator className="my-2" />
              <CardFooter className="pt-4">
                <Button className="w-full" variant="outline" asChild>
                  <Link to="/IntelligencePage">
                    Access Full Report
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </div>

          {}
          <div className="w-full lg:w-2/3">
            <Card className="h-full border-border/50 shadow-xl bg-card">
              <CardHeader>
                <CardTitle>Top 10 U.S. Hispanic Markets</CardTitle>
                <CardDescription>
                  Ranking by population size. Colorado stands as a pivotal hub in the Mountain West.
                </CardDescription>
              </CardHeader>
              <CardContent className="pl-0 pr-6 pb-6">
                <ChartContainer config={chartConfig} className="aspect-[4/3] w-full">
                  <BarChart accessibilityLayer data={chartData} layout="vertical" margin={{
                  left: 20,
                  right: 40,
                  top: 20,
                  bottom: 20
                }} barSize={32}>
                    <CartesianGrid horizontal={false} stroke="var(--border)" strokeDasharray="4 4" />
                    <YAxis dataKey="state" type="category" tickLine={false} tickMargin={10} axisLine={false} tickFormatter={value => value} width={100} className="text-sm font-medium fill-muted-foreground" />
                    <XAxis dataKey="population" type="number" hide />
                    <ChartTooltip cursor={{
                    fill: "var(--muted)",
                    opacity: 0.2
                  }} content={<ChartTooltipContent indicator="line" />} />
                    <Bar dataKey="population" layout="vertical" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.state === "Colorado" ? "var(--chart-2)" : "var(--chart-1)"} className="transition-all duration-300 hover:opacity-80" />)}
                      <LabelList dataKey="label" position="right" offset={8} className="fill-foreground font-mono text-xs font-bold" fontSize={12} />
                    </Bar>
                  </BarChart>
                </ChartContainer>
              </CardContent>
              <CardFooter className="flex-col items-start gap-2 text-sm border-t border-border/50 pt-4 bg-muted/20">
                <div className="flex gap-2 font-medium leading-none text-foreground">
                  Colorado is the fastest growing market in the region <TrendingUp className="h-4 w-4 text-secondary" />
                </div>
                <div className="leading-none text-muted-foreground">
                  Source: U.S. Census Bureau & COHCC Economic Analysis
                </div>
              </CardFooter>
            </Card>
          </div>
        </div>

        {}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[{
          title: "Download Data",
          desc: "Get the raw datasets for your own analysis.",
          action: "Download CSV",
          link: "#",
          icon: Activity
        }, {
          title: "Partner With Us",
          desc: "Leverage our insights for your business strategy.",
          action: "Become a Partner",
          link: "/JoinUsPage",
          icon: Users
        }, {
          title: "View Regional Map",
          desc: "Explore county-level data across Colorado.",
          action: "Open Map",
          link: "/IntelligencePage",
          icon: MapPin
        }].map((item, i) => <div key={i} data-index={i} className="flex flex-col p-6 rounded-xl border border-border/40 bg-card/30 hover:bg-card/50 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 rounded-md bg-primary/10 text-primary">
                  <item.icon className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-foreground">{item.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 flex-1">{item.desc}</p>
              <Button variant="ghost" className="w-fit p-0 h-auto hover:bg-transparent hover:text-secondary" asChild>
                <Link to={item.link} className="flex items-center gap-2">
                  {item.action} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </Button>
            </div>)}
        </div>
      </div>
    </section>;
}